import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { fullMotion } from '@/lib/experience';
import { sound } from '@/lib/sound';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-[background-color,color,border-color,box-shadow,transform,translate] duration-200 ease-(--ease-out) active:scale-[0.97] active:duration-75 focus-visible:shadow-(--ring-glow) disabled:pointer-events-none disabled:opacity-55 aria-busy:cursor-progress [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-200',
  {
    variants: {
      variant: {
        primary: 'fx-ripple-host bg-linear-to-b from-primary/90 to-primary text-primary-fg shadow-[0_1px_0_rgb(255_255_255/0.18)_inset,0_4px_12px_-4px_color-mix(in_oklab,var(--color-primary)_55%,transparent)] hover:-translate-y-px hover:from-primary-hover hover:to-primary-hover hover:shadow-[0_1px_0_rgb(255_255_255/0.18)_inset,0_8px_18px_-6px_color-mix(in_oklab,var(--color-primary)_60%,transparent)]',
        secondary: 'fx-ripple-host border border-border-strong bg-surface text-fg shadow-sm hover:-translate-y-px hover:border-primary/40 hover:bg-surface-2 hover:shadow-(--shadow-card) hover:[&_svg]:text-primary',
        ghost: 'text-fg-muted hover:bg-black/5 hover:text-fg dark:hover:bg-white/8 hover:[&_svg]:scale-110',
        danger: 'fx-ripple-host bg-danger text-white hover:-translate-y-px hover:bg-danger/90 shadow-[0_4px_12px_-4px_color-mix(in_oklab,var(--color-danger)_55%,transparent)]',
        'danger-outline': 'border border-danger/40 bg-surface text-danger hover:bg-danger-soft',
        link: 'text-primary underline-offset-4 hover:underline px-0 h-auto',
      },
      size: { sm: 'h-8 px-2.5 text-[13px]', md: 'h-9 px-3.5 text-sm', lg: 'h-10 px-5 text-sm', icon: 'size-9', 'icon-sm': 'size-8' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Shows a spinner and disables the button (prevents double submission). */
  loading?: boolean;
  /**
   * Why the action cannot run yet (e.g. "Enter the reason"). The button stays clickable, looks
   * muted, and a click explains instead of doing nothing — never a silently greyed-out button.
   */
  blocked?: string | false | null;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, blocked, children, type = 'button', onClick, title, ...props }, ref) => (
    <button
      ref={ref}
      type={blocked ? 'button' : type}
      className={cn(buttonVariants({ variant, size }), blocked && 'cursor-not-allowed opacity-55', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-disabled={blocked ? true : undefined}
      title={blocked || title}
      onClick={(e) => {
        if (blocked) {
          e.preventDefault();
          toast.warning(blocked, { id: 'form-problems' });
          return;
        }
        // Feedback on the main actions: a ripple from the pointer and a soft tap (when sounds are on).
        if (variant === undefined || variant === 'primary' || variant === 'danger' || variant === 'secondary') {
          if (variant !== 'secondary') sound.press();
          if (fullMotion() && e.clientX) ripple(e.currentTarget, e.clientX, e.clientY);
        }
        onClick?.(e);
      }}
      {...props}
    >
      {loading && <Loader2 className="fx-pop animate-spin" />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

function ripple(el: HTMLElement, x: number, y: number) {
  const r = el.getBoundingClientRect();
  const size = Math.max(r.width, r.height) * 2.2;
  const dot = document.createElement('span');
  dot.className = 'fx-ripple-dot';
  Object.assign(dot.style, { width: `${size}px`, height: `${size}px`, left: `${x - r.left - size / 2}px`, top: `${y - r.top - size / 2}px` });
  el.appendChild(dot);
  setTimeout(() => dot.remove(), 600);
}
